import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { DueItem, DueItemList, DueItemsAnfrage } from "@kundeninfo/api-client";
import { useTelefon } from "@kundeninfo/ui";
import { Abschnitt, Chip, Hinweis, Tabelle } from "@kundeninfo/ui/me-design";
import { KiKnopf } from "../../components/meHuellen.js";
import { hausfarbeChip } from "../../markenfarben.js";
import { readJson, rememberValue, userKey } from "../../browserMemory.js";
import { berlinerTag, tagBeschriftung } from "../tickets/ticketFormat.js";
import type { BoardFilter } from "../tickets/boardSpeicher.js";
import { amountText, canCreateTicket, compareDueItems, dueMark, plusDays, requestedItems, sameDayTicket } from "./dueItems.js";
import type { DueSource } from "./dueItems.js";
import { DUE_ITEM_TEXT } from "./dueItemTexts.js";
import type { DueItemsClient } from "./dueItemsClient.js";
import { DueSymbol } from "./DueSourceChips.js";
import { MILESTONE_TEXT } from "../milestones/milestoneTexts.js";
import "./Termine.css";

/**
 * ABSCHNITT „TERMINE“ DER LISTE (Spec D25, Empfehlung 584; Plan T18): über der Tickettabelle, vor den Fremdtickets — die
 * Tickettabelle und ihr Seitenladen bleiben unverändert. Fenster: offene Einträge der zugeschalteten Quellen von heute
 * bis heute + 14, dazu die überfälligen (`overdue=include`; vor heute liefert der Server nur OVERDUE, Ruling G1), Ordnung
 * „Schlechtestes oben“, höchstens 200 Zeilen und darüber ein Hinweis auf die Filter. Unternehmen und Kunde aus dem Filter
 * der Liste, dazu ihre Suche; Zuständig, Team und „nur verknüpfte“ wirken nur auf Tickets (D11), „Archiv“ nicht.
 *
 * Ohne zugeschaltete Quelle zeichnet der Abschnitt nichts und fragt nichts an (der Klient machte aus `sources: []` „alle
 * Quellen“, Review Aufgabe 2, H3). Nur die jüngste Antwort zeichnet; ein Fehler lässt den alten Stand stehen. Beim
 * Neuladen bleibt die Tabelle dasselbe Element (Scrollposition des Rollfelds der Liste, Hausregel).
 *
 * Kopf: „Termine“ mit der Zahl in der Pille und „n überfällig“ als Kennzeichen (flächig), zuklappbar, gemerkt unter
 * `ki-due-section:<userId>`. Spalten: Quelle (Symbol und Wort, am Telefon nur das Symbol), Nummer, Titel · Partner,
 * Unternehmen (Chip in der Hausfarbe aus dem Kalender der Antwort), Fällig (Tag und Kennzeichen: Fehlerton überschritten,
 * Warnton heute), Betrag (rechtsbündig), Ticket. Klick auf Nummer oder Titel → `onOpen` (Sprung bzw. Ticketdialog; der
 * Bereich entscheidet). Ticket-Spalte nach D26: liegt das Fälligkeitsticket am Tag des Objekts, steht seine Nummer da,
 * sonst „Ticket … · fällig …“ — beides öffnet das Ticket (`onOpenTicket`).
 *
 * Meilensteine (Aufgabe 8, D21): Unter dem Titel steht der Fortschritt „1/2 erledigt“; „+ Meilenstein“ steht im Kopf, sobald
 * die Quelle „Meilensteine“ angefragt ist — auch ohne Treffer (Ruling zu D24), nur mit `onCreateMilestone`.
 */

export const DUE_SECTION_ROWS = 200;
export const DUE_SECTION_DAYS = 14;

export type DueItemsSectionProps = {
  client: DueItemsClient; userId: string;
  /** Die anzufragenden Quellen (`requestSources`); leer = kein Abschnitt, keine Abfrage. */
  sources: readonly DueSource[];
  filter: BoardFilter; q: string; neuLaden?: number;
  onOpen: (item: DueItem) => void;
  /** Die Ticketnummer der Spalte „Ticket“ öffnet das Ticket; ohne den Rückruf steht sie als Text. */
  onOpenTicket?: (ticketId: string) => void;
  /** „+ Meilenstein“ im Kopf, solange die Quelle „Meilensteine“ angefragt ist (Aufgabe 8). */
  onCreateMilestone?: () => void;
  /** „Ticket anlegen“ in der Spalte „Ticket“ an Fremdeinträgen ohne Ticket (Aufgabe 12). */
  onCreateTicket?: (item: DueItem) => void;
};

const SECTION_KEY = "ki-due-section";

function readOpen(userId: string): boolean {
  return readJson(userKey(SECTION_KEY, userId)) !== false;
}

export function DueItemsSection(p: DueItemsSectionProps): ReactElement | null {
  const phone = useTelefon();
  const [data, setData] = useState<DueItemList | null>(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(() => readOpen(p.userId));
  // Der Berliner Tag der Uhr (F13) — wie die Liste; wechselt er, fragt der Abschnitt das neue Fenster an.
  const heute = berlinerTag(new Date().toISOString());
  // Die Anfrage aus Werten, nicht aus Objekten: ein neu gebautes, gleiches Quellen-Array lädt nicht neu.
  const sourcesKey = p.sources.join(",");
  const company = p.filter.company === "ALLE" ? "" : p.filter.company;
  const customer = p.filter.customer;
  const run = useRef(0);
  const load = useCallback(async () => {
    const current = ++run.current;
    const query: DueItemsAnfrage = { from: heute, to: plusDays(heute, DUE_SECTION_DAYS), sources: sourcesKey.split(","),
      overdue: true, open: true, ...(company !== "" ? { company } : {}), ...(customer !== "" ? { customer } : {}),
      ...(p.q !== "" ? { q: p.q } : {}) };
    try {
      const r = await p.client.listDueItems(query);
      if (current !== run.current) return;
      setData(r);
      setFailed(false);
    } catch {
      if (current === run.current) setFailed(true);
    }
  }, [p.client, heute, sourcesKey, company, customer, p.q]);
  useEffect(() => {
    if (sourcesKey === "") { run.current += 1; return; }
    void load();
  }, [load, sourcesKey, p.neuLaden]);

  if (sourcesKey === "" || (data === null && !failed)) return null;
  // G8: nur die angefragten Quellen — ein abgeschalteter Chip wirkt sofort, nicht erst mit der Antwort.
  const items = data === null ? [] : requestedItems(data.items, p.sources).sort(compareDueItems);
  const rows = items.slice(0, DUE_SECTION_ROWS);
  const count = data?.truncated === true ? data.total : items.length;
  const overdue = items.filter((i) => i.state === "OVERDUE").length;
  const calendars = data?.kalender ?? [];
  const T = DUE_ITEM_TEXT;
  const createMilestone = p.sources.includes("MILESTONE") ? p.onCreateMilestone : undefined;
  const ticketCell = (i: DueItem): ReactElement | null => {
    const create = p.onCreateTicket;
    if (i.ticket === null) {
      return create === undefined || !canCreateTicket(i) ? null
        : <KiKnopf kompakt onClick={() => create(i)}>{T.createTicket}</KiKnopf>;
    }
    const t = i.ticket;
    const text = sameDayTicket(i) ? t.displayNumber : T.ticketOther(t.displayNumber, t.dueDate);
    const openTicket = p.onOpenTicket;
    return openTicket === undefined ? <span>{text}</span>
      : <KiKnopf art="leise" className="ter__link" onClick={() => openTicket(t.id)}>{text}</KiKnopf>;
  };

  return (
    <div className="ter">
      <Abschnitt titel={T.sectionTitle} zaehler={count} offen={open}
        onUmgeschaltet={(d) => { setOpen(d.offen); rememberValue(userKey(SECTION_KEY, p.userId), JSON.stringify(d.offen)); }}
        aktionen={overdue > 0 || createMilestone !== undefined ? (
          <>
            {overdue > 0 && <Chip className="ter__overdue" statusArt="fehler">{T.sectionOverdue(overdue)}</Chip>}
            {createMilestone !== undefined && <KiKnopf kompakt onClick={createMilestone}>{MILESTONE_TEXT.create}</KiKnopf>}
          </>
        ) : undefined}>
        {failed && <Hinweis art="fehler">{T.loadError}</Hinweis>}
        {(items.length > DUE_SECTION_ROWS || data?.truncated === true) && <Hinweis art="warnung">{T.sectionLimit}</Hinweis>}
        {data !== null && rows.length === 0 && <p className="ter__empty">{T.empty}</p>}
        {rows.length > 0 && (
          <Tabelle stapeln>
            <table className="ter__table">
              <thead>
                <tr>
                  <th scope="col" className="ter__col-source">{T.columns.source}</th>
                  <th scope="col" className="ter__col-number">{T.columns.number}</th>
                  <th scope="col">{T.columns.titlePartner}</th>
                  <th scope="col" className="ter__col-company">{T.columns.company}</th>
                  <th scope="col" className="ter__col-due">{T.columns.due}</th>
                  <th scope="col" className="ter__col-amount" data-ausrichtung="rechts">{T.columns.amount}</th>
                  <th scope="col" className="ter__col-ticket">{T.columns.ticket}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((i) => {
                  const word = T.wordOf(i);
                  const mark = dueMark(i, heute);
                  const title = i.title !== "" ? i.title : i.reference ?? word;
                  return (
                    <tr key={i.key} data-key={i.key}>
                      <td>{phone
                        ? <span className="ter__source" role="img" aria-label={word}><DueSymbol source={i.source} /></span>
                        : <span className="ter__source"><DueSymbol source={i.source} />{word}</span>}</td>
                      <td>{i.reference === null ? T.none
                        : <KiKnopf art="leise" className="ter__link ter__number" onClick={() => p.onOpen(i)}>{i.reference}</KiKnopf>}</td>
                      <td><div className="ter__title-block">
                        <KiKnopf art="leise" className="ter__link" onClick={() => p.onOpen(i)}>{title}</KiKnopf>
                        {i.partner !== null && <span className="ter__partner">{i.partner.name}</span>}
                        {i.progress !== null && (
                          <span className="ter__progress">{MILESTONE_TEXT.progress(i.progress.done, i.progress.total)}</span>
                        )}
                      </div></td>
                      <td><Chip {...hausfarbeChip(calendars, i.companyShortCode)}>{i.companyShortCode}</Chip></td>
                      <td><div className="ter__due">
                        <span className="ter__day">{tagBeschriftung(i.date)}{i.time === null ? "" : ` ${i.time}`}</span>
                        {mark.art !== null && <Chip statusArt={mark.art}>{mark.text}</Chip>}
                      </div></td>
                      <td data-ausrichtung="rechts" className="ter__amount">{amountText(i) ?? T.none}</td>
                      <td>{ticketCell(i)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Tabelle>
        )}
      </Abschnitt>
    </div>
  );
}
